import type { ReactNode } from 'react';
import ReactDOM from 'react-dom';
import { X } from 'lucide-react';
import { useExitTransition } from '../hooks/useExitTransition';

/**
 * El drawer de selección de la app — ADR-2026-08-04-el-drawer-de-formato-se-comparte.
 *
 * Es el chrome que estrenó el picker de Formato de Interfaz V2 (`pages/v2/FormatPicker`)
 * y que ahora comparte con el de modos de "Editar marca":
 *
 *   • mobile  → bottom sheet con drag handle y header iOS (Cancelar · título · spacer).
 *   • desktop → panel lateral derecho de 630px que entra deslizando, con header de
 *               título + X y un divisor bajo el header.
 *
 * Sólo el chrome: el contenido lo pone quien lo usa, y el sheet no impone footer de
 * "Aceptar" —a diferencia de `components/opciones/OptionSheet`— porque estos pickers
 * commitean con el tap sobre la opción.
 *
 * No es `OptionSheet` con el footer apagado: ese sheet monta el panel de desktop
 * INLINE (sólo portalea el mobile), y desde una pantalla con `overflow` propio —la
 * grilla de productos de "Editar marca"— eso lo recorta. Acá se portalea todo.
 */
export function SheetLateral({
  open,
  onClose,
  titulo,
  altoPorContenido = false,
  children,
}: {
  open: boolean;
  onClose: () => void;
  titulo: string;
  /**
   * En MOBILE el sheet mide lo que su contenido en vez de ocupar la pantalla —
   * ADR-2026-08-04-el-sheet-de-marcas-mide-lo-que-su-lista.
   *
   * Por defecto va alto fijo, que es lo que necesitan los pickers cuyo contenido
   * llena la pantalla igual (formato, modos): con alto variable, el sheet saltaría
   * de tamaño al filtrar o al cambiar de paso. Para una lista corta —las marcas de
   * la cuenta— ese alto fijo deja media pantalla en blanco.
   *
   * El tope sigue siendo el mismo: con muchos ítems crece hasta donde crecía antes y
   * de ahí scrollea. En desktop no cambia nada; el panel lateral ya era `h-auto`.
   */
  altoPorContenido?: boolean;
  children: ReactNode;
}) {
  // Mantiene el sheet montado durante la animación de salida: sin esto, cerrar
  // desmonta en el acto y la transición inversa no llega a correr.
  const { mounted, animated } = useExitTransition(open, 300);

  if (!mounted) return null;

  return ReactDOM.createPortal(
    <>
      <div
        className={`fixed inset-0 z-[210] bg-black/40 transition-opacity duration-300 ${
          animated ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={onClose}
      />
      <div
        className={`fixed z-[211] bg-white flex flex-col shadow-2xl transition-transform duration-300 ease-out
          inset-x-0 bottom-0 rounded-t-[13px] ${
            altoPorContenido
              ? // Mide el contenido y usa el alto de siempre como TECHO: pasado ese
                // punto la lista scrollea adentro, igual que antes.
                'max-h-[calc(100dvh-1rem-env(safe-area-inset-top,0px))]'
              : 'h-[calc(100dvh-1rem-env(safe-area-inset-top,0px))]'
          }
          md:inset-x-auto md:left-auto md:top-4 md:right-4 md:bottom-4 md:h-auto md:w-[630px] md:rounded-[13px]
          ${
            animated
              ? 'translate-y-0 md:translate-x-0'
              : 'translate-y-full md:translate-y-0 md:translate-x-[calc(100%+32px)]'
          }`}
        style={{
          // En mobile la altura del sheet (en className) ya excluye el safe area
          // top — bottom-anclado sin zona blanca arriba.
          paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
        }}
      >
        {/* Drag handle — solo mobile. */}
        <div className="md:hidden py-2 shrink-0">
          <div className="w-9 h-[5px] bg-black/[0.15] rounded-full mx-auto" />
        </div>
        {/* Header iOS (mobile): Cancelar · título · spacer. */}
        <div className="md:hidden flex items-center justify-between px-4 pb-3 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="text-[15px] text-brand-primary active:opacity-50 transition-opacity w-20 text-left"
          >
            Cancelar
          </button>
          <h3 className="text-[15px] font-semibold text-brand-black/90">{titulo}</h3>
          <div className="w-20" />
        </div>
        {/* Header desktop: título + X. */}
        <div className="hidden md:flex items-center justify-between px-5 py-4 shrink-0">
          <h3 className="text-[17px] font-semibold text-brand-black/90">{titulo}</h3>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="hidden md:block mx-5 h-px bg-black/[0.08] shrink-0" />
        {/* `min-h-0` es lo que le permite encogerse dentro del flex cuando el sheet
            mide por contenido: sin eso el hijo no baja de su alto natural y el
            `max-h` del contenedor no llegaría a recortar nada. */}
        <div className="flex-1 min-h-0 overflow-y-auto px-4 md:px-5 pt-2 md:pt-4 pb-4">
          {children}
        </div>
      </div>
    </>,
    document.body,
  );
}

export default SheetLateral;
